import React, { createContext, useContext, useState, useEffect } from 'react'

export interface ImpersonatedUser {
  customerId: string
  email: string | null
  readOnly: boolean
}

interface ImpersonationContextProps {
  impersonatedUser: ImpersonatedUser | null
  setImpersonatedUser: (user: ImpersonatedUser) => void
  clearImpersonatedUser: () => void
}

const ImpersonationContext = createContext<
  ImpersonationContextProps | undefined
>(undefined)

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [impersonatedUser, setImpersonatedUserState] =
    useState<ImpersonatedUser | null>(null)

  useEffect(() => {
    const storedData = localStorage.getItem('impersonated_user')
    if (storedData) {
      const user = JSON.parse(storedData) as ImpersonatedUser
      setImpersonatedUserState(user)
    }
  }, [])

  const setImpersonatedUser = (user: ImpersonatedUser) => {
    setImpersonatedUserState(user)
    localStorage.setItem('impersonated_user', JSON.stringify(user))
    window.location.reload()
  }

  const clearImpersonatedUser = () => {
    setImpersonatedUserState(null)
    localStorage.removeItem('impersonated_user')
    window.location.reload()
  }

  return (
    <ImpersonationContext.Provider
      value={{ impersonatedUser, setImpersonatedUser, clearImpersonatedUser }}
    >
      {children}
    </ImpersonationContext.Provider>
  )
}

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext)
  if (context === undefined) {
    throw new Error(
      'useImpersonation must be used within an ImpersonationProvider'
    )
  }
  return context
}
